/**
 * GOAT Academy Custom Product Page JavaScript
 * Handles interactions, cart functionality, and dynamic behaviors
 */

(function() {
  'use strict';
  
  // Configuration
  const GOAT_CONFIG = {
    // Update these variant IDs with your actual Shopify product variant IDs
    variants: {
      annual: null,    // Replace with actual annual subscription variant ID
      onetime: null    // Replace with actual one-time purchase variant ID
    },
    scrollOffset: 100,
    animationDuration: 600
  };
  
  /**
   * Initialize the page when DOM is ready
   */
  function init() {
    // Auto-detect variant IDs from Shopify product object if available
    if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
      autoDetectVariants();
    }
    
    // Set up event listeners
    setupEventListeners();
    
    // Animate elements on scroll
    setupScrollAnimations();
    
    // Track page view
    trackPageView();
    
    console.log('GOAT Product Page initialized');
  }
  
  /**
   * Auto-detect variant IDs from Shopify product data
   */
  function autoDetectVariants() {
    // This function attempts to find variant IDs based on product options
    // You may need to customize this based on your product setup
    
    // If you're using Shopify's product object
    if (typeof product !== 'undefined' && product.variants) {
      product.variants.forEach(function(variant) {
        // Customize these conditions based on your variant titles/options
        if (variant.title.toLowerCase().includes('annual') || 
            variant.title.toLowerCase().includes('subscription')) {
          GOAT_CONFIG.variants.annual = variant.id;
        } else if (variant.title.toLowerCase().includes('one-time') || 
                   variant.title.toLowerCase().includes('onetime')) {
          GOAT_CONFIG.variants.onetime = variant.id;
        }
      });
    }
  }
  
  /**
   * Set up all event listeners
   */
  function setupEventListeners() {
    // FAQ toggle functionality is handled by inline onclick,
    // but we can enhance it here if needed
    
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href !== '#' && document.querySelector(href)) {
          e.preventDefault();
          smoothScrollTo(href);
        }
      });
    });
    
    // Track CTA button clicks
    document.querySelectorAll('.goat-cta-button').forEach(function(button) {
      button.addEventListener('click', function() {
        const variantType = this.getAttribute('onclick')?.match(/goatAddToCart\('(\w+)'\)/)?.[1];
        if (variantType) {
          trackEvent('CTA Click', { variant: variantType });
        }
      });
    });
  }
  
  /**
   * Add product to cart (exposed globally for inline onclick handlers)
   */
  window.goatAddToCart = function(variantType) {
    const variantId = GOAT_CONFIG.variants[variantType];
    
    if (!variantId) {
      console.error('Variant ID not found for type:', variantType);
      alert('Please configure the variant IDs in the JavaScript file. See implementation instructions.');
      return;
    }
    
    // Show loading state
    const button = event.target;
    const originalText = button.textContent;
    button.textContent = 'Adding to cart...';
    button.disabled = true;
    
    // Add to cart using Shopify Ajax API
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
        }]
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('Added to cart:', data);
      
      // Track conversion
      trackEvent('Add to Cart', { 
        variant: variantType,
        variantId: variantId 
      });
      
      // Update button state
      button.textContent = '✓ Added! View Cart';
      button.style.background = '#34a853';
      
      // Redirect to cart after a brief delay
      setTimeout(function() {
        window.location.href = '/cart';
      }, 1000);
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert('There was an error adding the item to your cart. Please try again.');
      
      // Reset button state
      button.textContent = originalText;
      button.disabled = false;
    });
  };
  
  /**
   * Toggle FAQ item (exposed globally for inline onclick handlers)
   */
  window.goatToggleFAQ = function(button) {
    const faqItem = button.closest('.goat-faq-item');
    const isActive = faqItem.classList.contains('active');
    
    // Close all FAQ items
    document.querySelectorAll('.goat-faq-item').forEach(function(item) {
      item.classList.remove('active');
    });
    
    // Toggle current item
    if (!isActive) {
      faqItem.classList.add('active');
      trackEvent('FAQ Opened', { 
        question: button.querySelector('span').textContent 
      });
    }
  };
  
  /**
   * Scroll to pricing section (exposed globally for inline onclick handlers)
   */
  window.goatScrollToPricing = function() {
    const pricingSection = document.querySelector('.goat-pricing-section');
    if (pricingSection) {
      smoothScrollTo(pricingSection);
      trackEvent('Scroll to Pricing', {});
    }
  };
  
  /**
   * Smooth scroll to element or selector
   */
  function smoothScrollTo(target) {
    const element = typeof target === 'string' ? 
      document.querySelector(target) : target;
    
    if (element) {
      const targetPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition - GOAT_CONFIG.scrollOffset;
      const duration = GOAT_CONFIG.animationDuration;
      let start = null;
      
      function animation(currentTime) {
        if (start === null) start = currentTime;
        const timeElapsed = currentTime - start;
        const run = ease(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      }
      
      function ease(t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      }
      
      requestAnimationFrame(animation);
    }
  }
  
  /**
   * Set up scroll-triggered animations
   */
  function setupScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('goat-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    // Observe elements for animation
    const animateElements = document.querySelectorAll(
      '.goat-benefit-card, .goat-forwho-card, .goat-testimonial-card, .goat-pricing-card'
    );
    
    animateElements.forEach(function(element) {
      observer.observe(element);
    });
  }
  
  /**
   * Track page view
   */
  function trackPageView() {
    trackEvent('Product Page View', {
      product: 'Stock Intelligence',
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * Generic event tracking function
   * Integrates with Google Analytics, Facebook Pixel, etc.
   */
  function trackEvent(eventName, eventData) {
    console.log('Track Event:', eventName, eventData);
    
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, eventData);
    }
    
    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', eventName, eventData);
    }
    
    // Shopify Analytics
    if (typeof ShopifyAnalytics !== 'undefined') {
      ShopifyAnalytics.lib.track(eventName, eventData);
    }
  }
  
  /**
   * Weekly price calculator utility
   */
  function calculateWeeklyPrice(annualPrice) {
    return (annualPrice / 52).toFixed(2);
  }
  
  /**
   * Format currency
   */
  function formatCurrency(amount) {
    return '$' + parseFloat(amount).toFixed(2);
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Expose utilities globally if needed
  window.GOAT = {
    calculateWeeklyPrice: calculateWeeklyPrice,
    formatCurrency: formatCurrency,
    trackEvent: trackEvent
  };
  
})();